// W36: thin wrapper over the generated contacts client.
//
// Types come from `./generated/contacts` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate`); the request path stays on
// the house core so the envelope, cursor pagination, Idempotency-Key, token
// refresh and ApiError mapping behave exactly as before.

import { http } from './_core/request.js';
import type { DashboardStat } from './_core/dashboard.js';
import type { Envelope } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/contacts/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

/**
 * A contact row, as `GET /v1/contacts` and `GET /v1/contacts/{id}` project it.
 *
 * W46 reconciled this against `services/contacts`: `opt_in` is a real boolean,
 * `locale` and `updated_at` are in the projection, and the per-channel consent
 * document is available separately as `opt_in_channels`. The four members are
 * spelled out here (rather than taken from `gen.ContactDto`) only until the
 * generator is next run against the reconciled service.
 */
export type Contact = Omit<gen.ContactDto, 'opt_in' | 'msisdn'> & {
  /** E.164. `null` for an email-only contact (4 October 2026): a contact has a number, an address or both. */
  msisdn: string | null;
  /**
   * Whether the contact may still be messaged on WhatsApp. Read from
   * {@link Contact.opt_in_channels} by the service using the same rule the
   * broadcast suppression list applies: only an explicit `false` opts out.
   */
  opt_in: boolean;
  /** The whole per-channel consent map, e.g. `{ whatsapp: false }`. */
  opt_in_channels: Record<string, boolean>;
  /**
   * SMS consent (4 October 2026): `true` agreed to SMS (marketing SMS may go), `false` opted out of SMS (no
   * SMS at all), `null` not recorded (service SMS such as reminders may go; marketing SMS may not). WhatsApp
   * consent never counts as SMS consent.
   */
  sms_opt_in?: boolean | null;
  /** Email consent (4 October 2026), the same three states as {@link Contact.sms_opt_in}. */
  email_opt_in?: boolean | null;
  locale: string | null;
  updated_at: string;
  /**
   * The contact's custom fields, keyed by definition key, with the typed
   * value: a string for text, select, url and date (`YYYY-MM-DD`), a number
   * for number, a boolean for boolean. Only keys that have a definition
   * ({@link listFields}) appear; a defined key with no value is absent.
   */
  custom_fields: Record<string, ContactFieldValue>;
};

/** A stored custom-field value. */
export type ContactFieldValue = string | number | boolean | null;

/** The six value types a custom field may hold; the lookup's `code`. */
export type ContactFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'url';

/**
 * One custom contact field (`GET /v1/contacts/fields`).
 *
 * `contacts.custom_fields` was a jsonb column the flow engine wrote and
 * nothing a person could see read. A definition per key is what makes it a
 * field: a label for the form, a type filters and segments compare on,
 * options for a choice. `key` and `type` are immutable after creation
 * (renaming a key would orphan every stored value; retyping one would
 * corrupt them).
 */
export interface ContactFieldDefinition {
  id: string;
  /** Stable snake_case identifier, `^[a-z][a-z0-9_]{0,63}$`; what `custom_fields` is keyed by. */
  key: string;
  label: string;
  type: ContactFieldType;
  /** The allowed values, for `select` only; null otherwise. */
  options: string[] | null;
  description: string | null;
  sort_order: number;
  /** Inactive hides the field from forms and exports without touching the data. */
  is_active: boolean;
  /** How many contacts in the organisation carry a value for this key. */
  contacts_with_value: number;
  created_at: string;
  updated_at: string;
}

/**
 * Body of {@link createField}. Fails 400 with `FIELD_KEY_INVALID`,
 * `FIELD_KEY_RESERVED` (name, email, msisdn, locale, tags, notes, opt_in and
 * the other base columns), `FIELD_TYPE_UNKNOWN`, `FIELD_OPTIONS_REQUIRED`
 * (select with no options) or `FIELD_LABEL_REQUIRED`; 409 `FIELD_KEY_TAKEN`.
 */
export interface CreateContactFieldRequest {
  key: string;
  label: string;
  type: ContactFieldType;
  /** Required for `select`, ignored otherwise. */
  options?: string[];
  description?: string;
  sort_order?: number;
}

/**
 * Body of {@link updateField}. Absent means "leave it". Sending `key` or
 * `type` is refused with 400 `FIELD_IMMUTABLE`.
 */
export interface UpdateContactFieldRequest {
  label?: string;
  options?: string[];
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}
export type ContactUpsertResult = gen.ContactUpsertResult;
export type ImportAccepted = gen.ImportAccepted;
export type ImportJob = Omit<gen.ImportJobDto, 'report'> & { report: ImportReport | null };
/**
 * What the file will do: the phone-number rule's verdict per row, and (10
 * September 2026) how the header row maps: `mapped_columns` (header to
 * target), `unknown_columns` (headers matching no base column and no custom
 * field by key or label) and `field_value_problems` (cells that did not fit
 * their field's type). The preview, and a finished job's `report`. The three
 * members are spelled out here until the generator is next run.
 */
export type ImportReport = gen.ImportReportDto & {
  mapped_columns: Record<string, string>;
  unknown_columns: string[];
  field_value_problems: { count: number; examples: ImportFieldProblem[] };
};
export type ImportFailedRow = gen.ImportFailedRowDto;
export { isPhoneNumberInvalid, type PhoneNumberInvalid } from './_core/phone.js';
export type OptInEvent = gen.OptInEventDto;

/**
 * `opt_in` sets the contact's WhatsApp consent on create and flips it on
 * update; the change is written to the same opt-in audit trail
 * `POST /v1/contacts/{id}/opt-out` writes to. (W46: the service used to drop
 * this field, so the console's opt-in switch did nothing.)
 */
export type UpsertContactRequest = Omit<gen.CreateContactRequest, 'msisdn'> & {
  /**
   * The number; may be left out when `email` is given (4 October 2026). With both, the number decides which
   * contact is meant, and an email-only contact with that address gains the number. Without either the call
   * is refused 400 `CONTACT_KEY_REQUIRED`; an address that cannot be one, 400 `EMAIL_INVALID`.
   */
  msisdn?: string | null;
  opt_in?: boolean;
  /** SMS consent: `true` agreed, `false` opted out; omit to leave it as it is. Journalled like `opt_in`. */
  sms_opt_in?: boolean;
  /** Email consent (4 October 2026), the same way. */
  email_opt_in?: boolean;
  /**
   * A merge: keys present are set, a key whose value is `null` is removed,
   * keys absent are left alone. Every key must have a definition
   * ({@link listFields}) and the value is read against its type: a number
   * field takes `42` or `"42"`, a boolean `true` or `"yes"`, a date any ISO
   * form, a select one of its options, a url an absolute http(s) link.
   * Refused 400 with `UNKNOWN_FIELD` (the known keys listed),
   * `FIELD_TYPE_MISMATCH` (naming the key and expected type) or
   * `FIELD_OPTION_UNKNOWN`.
   */
  custom_fields?: Record<string, ContactFieldValue>;
};

export type OptOutRequest = gen.OptOutRequest;

export interface ContactListOpts extends PageOpts {
  /**
   * Matches the name or the number, case-insensitively (8 September 2026;
   * before that it was a case-SENSITIVE substring, so "thandi" found nobody
   * called "Thandi"). A term with digits in it is also read as a phone number:
   * both a bare-digit substring and the whole number typed the local way
   * ("082 111 0001") find the canonical form the book stores.
   */
  search?: string;
  tag?: string;
  /** Filter to opted-in (`true`) or opted-out (`false`) contacts. */
  opt_in?: boolean;
  /**
   * The channel `opt_in` is read for: `whatsapp` (default), `sms` or `email` (4 October 2026). Naming a
   * channel also keeps to contacts reachable on it: an address for email, a number otherwise.
   */
  channel?: 'whatsapp' | 'sms' | 'email';
  /** Only contacts who explicitly agreed to `channel` (for SMS: may receive marketing SMS). */
  consented?: boolean;
  /**
   * Only contacts in this language (1 November 2026): a base code such as `af`, or several as `af,zu` for any of
   * them. It matches the base code of the contact's `locale`, so `en` finds `en`, `en-ZA`, `en_GB` and a locale
   * written as `English`. Names (`isiZulu`) are read too. Codes are those of `GET /v1/languages`.
   */
  language?: string;
  offset?: number;
  /**
   * Column to sort by. Anything else falls back to newest-first rather than
   * erroring: a sort is a preference, not a request that can fail.
   *
   * Sorted server-side, because the list is paged: sorting in the browser
   * sorts one page out of the whole book and calls it sorted by name.
   */
  sort?: 'name' | 'opt_in' | 'updated_at';
  order?: 'asc' | 'desc';
  /**
   * Custom-field filters, keyed by definition key, sent as `f.<key>` query
   * parameters and ANDed. An empty string means "has any value". Text,
   * select and url compare case-insensitively (exact), number and date
   * compare typed, boolean takes `'true'` or `'false'`. An unknown key is
   * refused with 400 `UNKNOWN_FIELD` rather than matching everybody.
   */
  fields?: Record<string, string>;
}

// ── Operations ─────────────────────────────────────────────────────────────

export function listContacts(opts?: ContactListOpts): Promise<Page<Contact>> {
  const query: Record<string, string | number | boolean | undefined> = { ...buildPageQuery(opts) };
  if (opts?.search !== undefined) query.search = opts.search;
  if (opts?.tag !== undefined) query.tag = opts.tag;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.opt_in !== undefined) query.opt_in = opts.opt_in;
  if (opts?.channel !== undefined) query.channel = opts.channel;
  if (opts?.consented !== undefined) query.consented = opts.consented;
  if (opts?.language) query.language = opts.language;
  if (opts?.sort !== undefined) query.sort = opts.sort;
  if (opts?.order !== undefined) query.order = opts.order;
  for (const [key, value] of Object.entries(opts?.fields ?? {})) query[`f.${key}`] = value;
  return http.get<Page<Contact>>('/v1/contacts', { query });
}

// ── Custom fields ──────────────────────────────────────────────────────────

/** The organisation's custom fields, ordered by `sort_order` then label. */
export function listFields(): Promise<Page<ContactFieldDefinition>> {
  return http.get<Page<ContactFieldDefinition>>('/v1/contacts/fields');
}

export function createField(req: CreateContactFieldRequest): Promise<Envelope<ContactFieldDefinition>> {
  return http.post<Envelope<ContactFieldDefinition>>('/v1/contacts/fields', { body: req });
}

/** Fails 404 `FIELD_NOT_FOUND`; 400 `FIELD_IMMUTABLE` when the body carries `key` or `type`. */
export function updateField(id: string, req: UpdateContactFieldRequest): Promise<Envelope<ContactFieldDefinition>> {
  return http.put<Envelope<ContactFieldDefinition>>(`/v1/contacts/fields/${encodeURIComponent(id)}`, { body: req });
}

/**
 * Removes the definition **and** strips the key from every contact in the
 * organisation: the values are gone. To hide a field and keep what it holds,
 * {@link updateField} with `is_active: false` instead. Fails 404 `FIELD_NOT_FOUND`.
 */
export function deleteField(id: string): Promise<void> {
  return http.delete<void>(`/v1/contacts/fields/${encodeURIComponent(id)}`);
}

export function upsertContact(req: UpsertContactRequest): Promise<Envelope<ContactUpsertResult>> {
  return http.post<Envelope<ContactUpsertResult>>('/v1/contacts', { body: req });
}

export function optOutContact(id: string, req?: OptOutRequest): Promise<void> {
  return http.post<void>(`/v1/contacts/${encodeURIComponent(id)}/opt-out`, { body: req ?? {} });
}

export interface ImportOptions {
  /** ISO 3166-1 alpha-2 to assume for numbers without a country code. Defaults to the organisation's default country. */
  default_country?: string;
  /**
   * Create a `text` custom field for every header that matched nothing
   * before the rows are read, so those columns import too. Ignored by
   * {@link previewImport}, which only reports them as `unknown_columns`.
   */
  create_fields?: boolean;
}

function importForm(file: File, opts?: ImportOptions): FormData {
  const form = new FormData();
  form.append('file', file);
  if (opts?.default_country) form.append('default_country', opts.default_country);
  if (opts?.create_fields) form.append('create_fields', 'true');
  return form;
}

/** One cell the import could not fit to its field's type; stored as text and counted. */
export interface ImportFieldProblem {
  row: number;
  /** The definition key the column mapped to. */
  column: string;
  value: string;
  reason: string;
}

/** Accepted import, plus what the header row mapped to. */
export type ImportAcceptedWithMapping = ImportAccepted & {
  /** Header to target: `msisdn`, `name`, `email`, `locale`, `tags`, `notes`, `field:<key>` or `ignored`. `"1".."5"` for a headerless file. */
  mapped_columns: Record<string, string>;
  unknown_columns: string[];
  /** The headers `create_fields` turned into definitions on this call. */
  created_fields: string[];
};

/**
 * Queue the import. Every number goes through the one phone-number rule with
 * the chosen country: `0615360809` becomes `+27615360809` under South Africa,
 * a number with its own code keeps it, and a row that cannot be read is
 * skipped, counted and listed on the job's `report`. Preview first with
 * {@link previewImport} to show the person what will be assumed.
 */
export function importContacts(file: File, opts?: ImportOptions): Promise<Envelope<ImportAcceptedWithMapping>> {
  return http.post<Envelope<ImportAcceptedWithMapping>>('/v1/contacts/import', { body: importForm(file, opts) });
}

/** What {@link importContacts} would do with this file, without writing anything. */
export function previewImport(file: File, opts?: ImportOptions): Promise<Envelope<ImportReport>> {
  return http.post<Envelope<ImportReport>>('/v1/contacts/import/preview', { body: importForm(file, opts) });
}

export function getImportJob(id: string): Promise<Envelope<ImportJob>> {
  return http.get<Envelope<ImportJob>>(`/v1/contacts/import/${encodeURIComponent(id)}`);
}

export function listOptInEvents(since?: string): Promise<Envelope<OptInEvent[]>> {
  return http.get<Envelope<OptInEvent[]>>('/v1/admin/audit/opt-in', {
    query: since ? { since } : undefined
  });
}

// ── Single contact (W46) ───────────────────────────────────────────────────

/** Fails with 404 `CONTACT_NOT_FOUND`. */
export function getContact(id: string): Promise<Envelope<Contact>> {
  return http.get<Envelope<Contact>>(`/v1/contacts/${encodeURIComponent(id)}`);
}

/**
 * Removes the contact from the book (2 October 2026). The number stays with
 * everything else cleared, the person is opted out on the consent trail, and
 * the contact disappears from every list. Adding the number again brings the
 * person back, still opted out. To forget a person entirely (a POPIA request),
 * use {@link eraseContact}.
 *
 * Fails with 404 `CONTACT_NOT_FOUND`.
 */
export function deleteContact(id: string): Promise<void> {
  return http.delete<void>(`/v1/contacts/${encodeURIComponent(id)}`);
}

/** Alias of {@link deleteContact}, named for what it does. */
export const removeContact = deleteContact;

export interface EraseResult {
  erasure_id: string;
  /** Whether the number was in the book. */
  contact_deleted: boolean;
  /**
   * Whether every other service (inbox, flows, broadcasts, billing, voice,
   * WhatsApp sending) has been told. When false it is retried within a minute.
   */
  announced: boolean;
}

export interface EraseContactRequest {
  /** The contact's number typed again, in any spelling the organisation would read the same way. */
  confirm_msisdn?: string;
  /** For an email-only contact, its address typed again instead (4 October 2026). */
  confirm_email?: string;
  /** Why, for the organisation's own record (for example "POPIA request by email"). */
  reason?: string;
}

/**
 * Erases a person: the contact, its consent trail, conversations, files they
 * sent, flow runs, recordings and transcripts are deleted, and the number is
 * taken off broadcast, billing and sending records. Only a one-way hash is
 * kept, so the number stays opted out. Cannot be undone.
 *
 * Fails with 400 `ERASE_CONFIRMATION_MISMATCH` when `confirm_msisdn` is not
 * this contact's number, and 404 `CONTACT_NOT_FOUND`.
 */
export function eraseContact(id: string, req: EraseContactRequest): Promise<Envelope<EraseResult>> {
  return http.post<Envelope<EraseResult>>(`/v1/contacts/${encodeURIComponent(id)}/erase`, { body: req });
}

export interface EraseNumberRequest {
  msisdn?: string;
  /** Erase an email-only person by address instead of a number (4 October 2026). */
  email?: string;
  reason?: string;
  /** Two-letter country assumed for a number typed without a code; the organisation's default when absent. */
  default_country?: string;
}

/**
 * Erases everything held about a number, whether or not it is in the book
 * (a person who was removed, or who only ever wrote in). Same effect as
 * {@link eraseContact}. Fails with 400 `PHONE_NUMBER_INVALID`.
 */
export function eraseNumber(req: EraseNumberRequest): Promise<Envelope<EraseResult>> {
  return http.post<Envelope<EraseResult>>('/v1/contacts/erase', { body: req });
}

// ── Segments (W46) ─────────────────────────────────────────────────────────
// Hand-typed until the generator is next run against the reconciled service.

export interface Segment {
  id: string;
  name: string;
  description: string | null;
  /**
   * The stored rules document. `services/contacts` understands `tags`
   * (string[]: a contact matching ANY of them is in), `search` (substring of
   * msisdn or name), `opt_in` (boolean), `fields` (an array of
   * {@link SegmentFieldRule} over the custom fields, ANDed) and `languages`
   * (string[], 1 November 2026: "language is", a contact whose locale is in ANY
   * of these base codes, read as {@link ContactListOpts.language} reads one);
   * anything else is stored verbatim and does not narrow
   * {@link Segment.contact_count}.
   */
  rules: Record<string, unknown>;
  /** Contacts matching `rules` when the segment was last written. */
  contact_count: number | null;
  /** When `contact_count` was computed. */
  materialised_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One custom-field rule in a segment's `fields` array, typed per the field's
 * definition. `value` is an array for `in`, absent for `is_set` and
 * `is_empty`. A rule naming an unknown key or op is ignored (it does not
 * narrow), never treated as "matches nobody".
 */
export interface SegmentFieldRule {
  key: string;
  op: 'eq' | 'neq' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'is_set' | 'is_empty' | 'in';
  value?: ContactFieldValue | ContactFieldValue[];
}

export interface UpsertSegmentRequest {
  name: string;
  description?: string;
  /** See {@link Segment.rules}. For example `{ languages: ['af'], tags: ['vip'] }`. */
  rules?: Record<string, unknown>;
}

export function listSegments(): Promise<Envelope<Segment[]>> {
  return http.get<Envelope<Segment[]>>('/v1/segments');
}

export function getSegment(id: string): Promise<Envelope<Segment>> {
  return http.get<Envelope<Segment>>(`/v1/segments/${encodeURIComponent(id)}`);
}

/** Fails with 409 `SEGMENT_NAME_TAKEN`: segment names are unique per tenant. */
export function createSegment(req: UpsertSegmentRequest): Promise<Envelope<Segment>> {
  return http.post<Envelope<Segment>>('/v1/segments', { body: req });
}

export function updateSegment(id: string, req: UpsertSegmentRequest): Promise<Envelope<Segment>> {
  return http.put<Envelope<Segment>>(`/v1/segments/${encodeURIComponent(id)}`, { body: req });
}

export function deleteSegment(id: string): Promise<void> {
  return http.delete<void>(`/v1/segments/${encodeURIComponent(id)}`);
}

/** Contact summary. `opted_in` is the reachable audience a broadcast would hit. */
export type ContactsDashboardSummary = gen.ContactsDashboardSummary & DashboardStat;

/** Total contacts, contacts added this window vs last, and the opted-in count. */
export function getContactSummary(windowHours?: number): Promise<Envelope<ContactsDashboardSummary>> {
  return http.get<Envelope<ContactsDashboardSummary>>('/v1/contacts/summary', {
    query: windowHours === undefined ? undefined : { window_hours: windowHours }
  });
}

/**
 * Filters for an export. The same three the list honours, so "export what I am
 * looking at" means what it says.
 */
export interface ContactExportOpts {
  search?: string;
  tag?: string;
  opt_in?: boolean;
  /** The same language filter {@link listContacts} takes (1 November 2026). */
  language?: string;
  /** The same `f.<key>` filters {@link listContacts} takes. */
  fields?: Record<string, string>;
  /**
   * `csv` (default) streams and has no row limit. `xlsx` is a real workbook,
   * assembled server-side and therefore capped: past 100 000 rows the service
   * answers 400 `TOO_LARGE_FOR_XLSX` and asks for CSV.
   */
  format?: 'csv' | 'xlsx';
}

/**
 * Downloads the contact book as CSV or as an .xlsx workbook.
 *
 * Fetched rather than linked so the Authorization header goes with it: the
 * export is a normal authenticated GET, and a bare `<a href>` would arrive
 * without a token. The CSV response is streamed by the service, so this holds
 * one Blob rather than a parsed array.
 */
export async function exportContacts(opts?: ContactExportOpts): Promise<Blob> {
  const query = new URLSearchParams();
  if (opts?.search) query.set('search', opts.search);
  if (opts?.tag) query.set('tag', opts.tag);
  if (opts?.opt_in !== undefined) query.set('opt_in', String(opts.opt_in));
  if (opts?.language) query.set('language', opts.language);
  for (const [key, value] of Object.entries(opts?.fields ?? {})) query.set(`f.${key}`, value);
  if (opts?.format) query.set('format', opts.format);
  const suffix = query.toString();
  return http.getBlob(`/v1/contacts/export${suffix ? `?${suffix}` : ''}`);
}

// ── Tags ───────────────────────────────────────────────────────────────────

/**
 * One tag in the organisation's vocabulary.
 *
 * Tags are how this product groups contacts, so a tag is an audience: `count`
 * is how many people carry it and `opted_in` is how many of those a broadcast
 * would actually reach.
 */
export interface TagSummary {
  tag: string;
  count: number;
  opted_in: number;
}

/**
 * The whole tag vocabulary, with counts.
 *
 * The console used to build its tag filter from whatever happened to be on the
 * page it had fetched, so a tag used only by contacts further down the book was
 * not offered at all.
 */
export function listTags(): Promise<Page<TagSummary>> {
  return http.get<Page<TagSummary>>('/v1/contacts/tags');
}

export interface BulkTagRequest {
  tag: string;
  action: 'add' | 'remove';
  contact_ids: string[];
}

export interface BulkTagResult {
  tag: string;
  action: 'add' | 'remove';
  /** Contacts found for the ids given. */
  matched: number;
  /** Of those, how many actually changed: adding a tag someone already has is a no-op. */
  changed: number;
}

/**
 * Add or remove one tag across many contacts in a single call.
 *
 * Tagging is how a group is assembled, and it used to mean rewriting each
 * contact's whole tag array one at a time: forty round trips for forty people,
 * and a race with anyone else editing them.
 */
export function bulkTag(req: BulkTagRequest): Promise<Envelope<BulkTagResult>> {
  return http.post<Envelope<BulkTagResult>>('/v1/contacts/tags/bulk', { body: req });
}

/** One entry in a contact's consent trail. */
export type ConsentEvent = gen.ConsentEventDto;

/**
 * The consent trail for one contact, newest first.
 *
 * `opt_in_events` has been written since the service shipped and only the
 * platform-admin audit route ever read it, so the tenant who has to answer a
 * POPIA request could see one boolean and nothing else.
 */
export function listConsentHistory(contactId: string): Promise<Envelope<ConsentEvent[]>> {
  return http.get<Envelope<ConsentEvent[]>>(
    `/v1/contacts/${encodeURIComponent(contactId)}/consent`
  );
}

// ── Change history (4 October 2026) ────────────────────────────────────────

/**
 * One change to a contact's details, with what was there before.
 *
 * A contact's details are never changed silently: on 3 October 2026 a flow's
 * Update contact step replaced a customer's name "Stanley" with "Stan" and
 * kept nothing. Every change to a name, email address, language or custom
 * field is recorded here; for the first three the old value is also kept in
 * the custom fields `previous_name`, `previous_email` and `previous_language`.
 */
export interface ContactChange {
  /* The service leaves a null out of the JSON, so every nullable member may be absent. */
  id: string;
  /** `name`, `email`, `locale` or `field:<custom key>`. */
  field: string;
  /** What was there before; for `kept`, what stayed. */
  old_value?: string | null;
  /** What it was changed to; for `kept`, what the customer declined. */
  new_value?: string | null;
  /**
   * `flow` (a flow's Update contact step), `console` (a person; see
   * `created_by`), `api` or `undo`.
   */
  source: 'flow' | 'console' | 'api' | 'undo' | (string & {});
  /** What made it, e.g. "Knowledge base assistant (run 01a1...)" for a flow. */
  source_ref?: string | null;
  /** `changed`, or `kept` when the flow asked the customer and they said no. */
  outcome: 'changed' | 'kept' | (string & {});
  /** `customer` when the customer said yes in the chat; the person, for a console change. */
  confirmed_by?: string | null;
  /** Null until someone on the team marks an automated (`flow`) change seen. */
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  occurred_at: string;
  created_by?: string | null;
}

export interface ContactChangesPage {
  data: ContactChange[];
  meta: { count: number; unreviewed: number };
}

/**
 * A contact's change history, newest first, at most 200 rows. `meta.unreviewed`
 * counts the automated changes nobody has looked at yet. Fails 404
 * `CONTACT_NOT_FOUND`.
 */
export function listContactChanges(contactId: string): Promise<ContactChangesPage> {
  return http.get<ContactChangesPage>(`/v1/contacts/${encodeURIComponent(contactId)}/changes`);
}

/** Marks every unreviewed change on the contact as seen. */
export function reviewContactChanges(contactId: string): Promise<Envelope<{ reviewed: number }>> {
  return http.post<Envelope<{ reviewed: number }>>(
    `/v1/contacts/${encodeURIComponent(contactId)}/changes/review`,
    { body: {} }
  );
}

/**
 * Puts back what a change replaced. Owner and admins only (an agent gets 403
 * `ADMIN_ROLE_REQUIRED`). Fails 409 `CHANGED_SINCE` when the field was changed
 * again since (`error.current` in the body is what it holds now), 409
 * `NOTHING_TO_UNDO` when the change replaced nothing (or was declined), and
 * 404 `CHANGE_NOT_FOUND`.
 */
export function undoContactChange(contactId: string, changeId: string): Promise<Envelope<{ field: string; value: string | null }>> {
  return http.post<Envelope<{ field: string; value: string | null }>>(
    `/v1/contacts/${encodeURIComponent(contactId)}/changes/${encodeURIComponent(changeId)}/undo`,
    { body: {} }
  );
}

/** One contact matched by a segment's rules. */
export type SegmentMember = gen.SegmentMemberDto;

export interface SegmentMembersPage {
  data: SegmentMember[];
  meta: {
    count: number;
    total: number;
    limit: number;
    /**
     * False when the segment's rules restrict nothing, so it matches the whole
     * book. Fine to preview, refused as a broadcast audience: an unnarrowed
     * segment is what a malformed rule document produces, and "send to
     * everybody" is almost never what was meant.
     */
    narrowed: boolean;
  };
}

/**
 * The contacts a segment currently matches.
 *
 * `Segment.rules` was a jsonb column nothing evaluated, so segments were names
 * attached to documents no code understood and broadcasts could not use one as
 * an audience.
 */
export function listSegmentMembers(
  segmentId: string,
  opts?: { limit?: number; offset?: number }
): Promise<SegmentMembersPage> {
  const query: Record<string, string | number | undefined> = {};
  if (opts?.limit !== undefined) query.limit = opts.limit;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<SegmentMembersPage>(
    `/v1/segments/${encodeURIComponent(segmentId)}/members`,
    { query }
  );
}
